import { useEffect, useMemo, useState } from "react";
import {
  KonditionsmatrixSchema,
  type Konditionsmatrix,
  type KonditionsmatrixEntry,
} from "@bidi-handel/core/schemas";
import { api, ApiError } from "../../api/client";
import { useApi } from "../../hooks/useApi";
import { useTenant } from "../../app/TenantContext";

/**
 * Konditionsmatrix-Editor: Tabelle aller Einträge mit Inline-Edit, Add-Row,
 * Delete + atomisches Save. Validation: jeder Eintrag braucht entweder
 * `warengruppe` ODER `artikelCode` (Schema-Refine in core).
 */

// Draft mit Stable-Key für React. Im Schema gibt's keinen Identifier,
// also generieren wir eine Client-ID nur für UI-State.
interface DraftEntry extends KonditionsmatrixEntry {
  _key: string;
}

let keyCounter = 0;
const nextKey = () => `k${++keyCounter}-${Date.now()}`;

function emptyEntry(): DraftEntry {
  return {
    _key: nextKey(),
    kundeId: "",
    warengruppe: undefined,
    artikelCode: undefined,
    rabattProzent: undefined,
    sonderpreis: undefined,
    bemerkung: undefined,
  };
}

function withKeys(entries: Konditionsmatrix): DraftEntry[] {
  return entries.map((e) => ({ ...e, _key: nextKey() }));
}

function stripKeys(drafts: DraftEntry[]): Konditionsmatrix {
  return drafts.map(({ _key, ...rest }) => {
    void _key;
    return rest;
  });
}

export function KonditionenPage() {
  const { tenantId } = useTenant();
  const { state, refetch } = useApi(
    () => api.getKonditionsmatrix(tenantId),
    [tenantId]
  );
  const [drafts, setDrafts] = useState<DraftEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (state.status === "ok") {
      setDrafts(withKeys(state.data));
      setSaved(false);
    }
  }, [state.status, state.data]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return drafts;
    return drafts.filter(
      (e) =>
        e.kundeId.toLowerCase().includes(q) ||
        (e.warengruppe ?? "").toLowerCase().includes(q) ||
        (e.artikelCode ?? "").toLowerCase().includes(q)
    );
  }, [drafts, filter]);

  function update(key: string, patch: Partial<KonditionsmatrixEntry>) {
    setDrafts((ds) => ds.map((d) => (d._key === key ? { ...d, ...patch } : d)));
    setSaved(false);
  }
  function remove(key: string) {
    setDrafts((ds) => ds.filter((d) => d._key !== key));
    setSaved(false);
  }
  function add() {
    setDrafts((ds) => [emptyEntry(), ...ds]);
    setSaved(false);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const cleaned: Konditionsmatrix = stripKeys(drafts).map((e) => ({
        kundeId: e.kundeId.trim(),
        warengruppe: emptyToUndef(e.warengruppe),
        artikelCode: emptyToUndef(e.artikelCode),
        rabattProzent:
          typeof e.rabattProzent === "number" && !Number.isNaN(e.rabattProzent)
            ? e.rabattProzent
            : undefined,
        sonderpreis:
          typeof e.sonderpreis === "number" && !Number.isNaN(e.sonderpreis)
            ? e.sonderpreis
            : undefined,
        bemerkung: emptyToUndef(e.bemerkung),
      }));
      const validated = KonditionsmatrixSchema.parse(cleaned);
      const persisted = await api.putKonditionsmatrix(tenantId, validated);
      setDrafts(withKeys(persisted));
      setSaved(true);
      refetch();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { hint?: string; error?: string; issues?: unknown[] } | null;
        setError(
          body?.issues
            ? body.issues.map((i: unknown) => JSON.stringify(i)).join(" · ")
            : (body?.hint ?? body?.error ?? err.message)
        );
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(String(err));
      }
    } finally {
      setSaving(false);
    }
  }

  if (state.status === "loading")
    return <p className="text-sm text-slate-500">Lade Konditionsmatrix …</p>;
  if (state.status === "error")
    return <ErrorBanner error={state.error.message} onRetry={refetch} />;

  const dirty = state.status === "ok" && !sameLength(drafts, state.data);
  const kundenIds = Array.from(new Set(drafts.map((d) => d.kundeId).filter(Boolean))).sort();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs text-slate-500">
            {drafts.length} Eintrag{drafts.length === 1 ? "" : "e"}
            {kundenIds.length > 0 && ` · Kunden: ${kundenIds.join(", ")}`}
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            placeholder="Filter KundeId / Warengruppe / Code …"
            className="input w-72"
          />
          <button
            type="button"
            onClick={add}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            + Neuer Eintrag
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-3 py-2">KundeId</th>
              <th className="px-3 py-2">Warengruppe</th>
              <th className="px-3 py-2">ArtikelCode</th>
              <th className="px-3 py-2 text-right">Rabatt %</th>
              <th className="px-3 py-2 text-right">Sonderpreis</th>
              <th className="px-3 py-2">Bemerkung</th>
              <th className="w-12 px-2 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-500">
                  {filter
                    ? "Keine Eintraege passen zum Filter."
                    : "Noch keine Konditionen — auf '+ Neuer Eintrag' klicken."}
                </td>
              </tr>
            )}
            {filtered.map((row) => (
              <tr key={row._key}>
                <td className="px-2 py-1">
                  <input
                    type="text"
                    value={row.kundeId}
                    onChange={(e) => update(row._key, { kundeId: e.currentTarget.value })}
                    className="input w-full font-mono text-xs"
                    placeholder="acme"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="text"
                    value={row.warengruppe ?? ""}
                    onChange={(e) =>
                      update(row._key, { warengruppe: e.currentTarget.value || undefined })
                    }
                    className="input w-full text-xs"
                    placeholder="rohbau"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="text"
                    value={row.artikelCode ?? ""}
                    onChange={(e) =>
                      update(row._key, { artikelCode: e.currentTarget.value || undefined })
                    }
                    className="input w-full font-mono text-xs"
                    placeholder="ROH-KS-240"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={row.rabattProzent ?? ""}
                    onChange={(e) =>
                      update(row._key, {
                        rabattProzent:
                          e.currentTarget.value === ""
                            ? undefined
                            : Number(e.currentTarget.value),
                      })
                    }
                    className="input w-20 text-right text-xs"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={row.sonderpreis ?? ""}
                    onChange={(e) =>
                      update(row._key, {
                        sonderpreis:
                          e.currentTarget.value === ""
                            ? undefined
                            : Number(e.currentTarget.value),
                      })
                    }
                    className="input w-24 text-right text-xs"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="text"
                    value={row.bemerkung ?? ""}
                    onChange={(e) =>
                      update(row._key, { bemerkung: e.currentTarget.value || undefined })
                    }
                    className="input w-full text-xs"
                  />
                </td>
                <td className="px-2 py-1 text-center">
                  <button
                    type="button"
                    onClick={() => remove(row._key)}
                    className="rounded-md p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                    aria-label="Loeschen"
                    title="Loeschen"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-md bg-amber-50 p-3 text-xs text-amber-800">
        <strong>Regel:</strong> Pro Eintrag muss entweder eine Warengruppe ODER
        ein ArtikelCode gesetzt sein. Sind beide leer, schlägt das Speichern fehl.
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm">
          <p className="font-medium text-rose-800">Speichern fehlgeschlagen</p>
          <p className="mt-1 font-mono text-xs text-rose-700">{error}</p>
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {saved && (
          <span className="text-xs font-medium text-emerald-700">✓ gespeichert</span>
        )}
        {dirty && !saved && (
          <span className="text-xs font-medium text-amber-700">ungespeichert</span>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
        >
          {saving ? "Speichere …" : "Speichern"}
        </button>
      </div>
    </div>
  );
}

function emptyToUndef(s: string | undefined): string | undefined {
  if (s === undefined) return undefined;
  const t = s.trim();
  return t === "" ? undefined : t;
}

function sameLength(a: unknown[], b: unknown[]): boolean {
  return a.length === b.length;
}

function ErrorBanner({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="rounded-md border border-rose-200 bg-rose-50 p-4">
      <p className="text-sm font-medium text-rose-800">Konditionsmatrix nicht ladbar</p>
      <p className="mt-1 font-mono text-xs text-rose-700">{error}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 rounded-md border border-rose-300 bg-white px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
      >
        Nochmal versuchen
      </button>
    </div>
  );
}
